import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ASSETS, PUMP_URL, X_URL, TELEGRAM_URL, TIKTOK_URL } from "../../data/pusheen";
import { useSound } from "../../sound/SoundContext";
import { burstFromEvent } from "../../lib/burst";

const NAV = [
  { label: "Lore", href: "#lore" },
  { label: "Pet", href: "#pet" },
  { label: "Gallery", href: "#gallery" },
];

export default function Navbar() {
  const { play } = useSound();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    play("boop");
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const logoTap = (e) => {
    play("sparkle");
    burstFromEvent(e, { count: 16, kinds: ["heart", "star"] });
  };

  return (
    <motion.nav
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: "spring", stiffness: 120, damping: 16 }}
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "py-2 bg-pusheen-mint/85 backdrop-blur-md shadow-[0_4px_0_rgba(74,55,40,0.08)]" : "py-4 bg-transparent"
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-3">
        <button
          onClick={logoTap}
          data-testid="nav-logo"
          className="flex items-center gap-2 group"
          aria-label="Pusheen"
        >
          <img
            src={ASSETS.headerLogo}
            alt="Pusheen the Cat"
            className="h-10 sm:h-12 w-auto transition-transform group-hover:-rotate-3 group-hover:scale-105"
          />
        </button>

        <div className="hidden md:flex items-center gap-1">
          {NAV.map((n) => (
            <button
              key={n.href}
              onClick={() => scrollTo(n.href)}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className="px-4 py-2 rounded-full font-display font-bold text-pusheen-brown text-lg hover:bg-white/60 hover:-translate-y-0.5 transition-all"
            >
              {n.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <a
            href={TELEGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => play("pop")}
            data-testid="nav-telegram-button"
            aria-label="Join us on Telegram"
            className="bg-white text-pusheen-brown border-[3px] border-pusheen-brown rounded-full p-2.5 shadow-[3px_3px_0_#4A3728] hover:bg-pusheen-cream active:translate-y-[3px] active:shadow-none transition-all"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true">
              <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" />
            </svg>
          </a>
          <a
            href={X_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => play("pop")}
            data-testid="nav-x-button"
            aria-label="Follow on X"
            className="bg-white text-pusheen-brown border-[3px] border-pusheen-brown rounded-full p-2.5 shadow-[3px_3px_0_#4A3728] hover:bg-pusheen-cream active:translate-y-[3px] active:shadow-none transition-all"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
          </a>
          <a
            href={TIKTOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => play("pop")}
            data-testid="nav-tiktok-button"
            aria-label="Follow on TikTok"
            className="bg-white text-pusheen-brown border-[3px] border-pusheen-brown rounded-full p-2.5 shadow-[3px_3px_0_#4A3728] hover:bg-pusheen-cream active:translate-y-[3px] active:shadow-none transition-all"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true">
              <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
            </svg>
          </a>
          <a
            href={PUMP_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => play("yay")}
            data-testid="nav-buy-button"
            className="bg-pusheen-pink text-pusheen-brown border-[3px] border-pusheen-brown rounded-full px-4 sm:px-6 py-2 font-display font-extrabold text-base sm:text-lg shadow-[3px_3px_0_#4A3728] hover:bg-pusheen-pinklight hover:-translate-y-0.5 active:translate-y-[3px] active:shadow-none transition-all whitespace-nowrap"
          >
            Buy <span className="hidden xs:inline">$PUSHEEN</span><span className="xs:hidden">$PUSH</span>
          </a>
        </div>
      </div>
    </motion.nav>
  );
}
